import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import type { TemplateSchemaType } from "features/templates/schemas";
import type { CreatedTemplate } from "features/templates/types";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, patch, post } from "common/helpers/HTTP";
import type { DistributiveOmit } from "common/types";

type TemplateUpdates = Partial<TemplateSchemaType>;

async function createTemplateRequest(
  workspaceId: string,
  template: TemplateSchemaType,
): Promise<CreatedTemplate> {
  return (
    await post(`workspaces/${workspaceId}/templates`, {
      ...template,
      attachments:
        "attachments" in template
          ? template.attachments.map(({ id }) => id)
          : undefined,
    })
  ).template;
}

async function updateTemplateRequest(
  workspaceId: string,
  templateId: string,
  updates: TemplateUpdates,
): Promise<DistributiveOmit<CreatedTemplate, "metrics">> {
  return (
    await patch(`workspaces/${workspaceId}/templates/${templateId}`, {
      ...updates,
      attachments:
        "attachments" in updates
          ? updates.attachments.map(({ id }) => id)
          : undefined,
    })
  ).template;
}

async function deleteTemplateRequest(workspaceId: string, templateId: string) {
  await del(`workspaces/${workspaceId}/templates/${templateId}`);
}

export default function useTemplateMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { templatesKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: createTemplate, isPending: isCreatingTemplate } =
    useMutation({
      mutationFn: ({ template }: { template: TemplateSchemaType }) =>
        createTemplateRequest(workspaceId, template),
      onError: () => toast.error("Failed to create template"),
      onSuccess: (template) => {
        queryClient.setQueryData(
          templatesKeys.list,
          (prevTemplates: CreatedTemplate[]) => [template, ...prevTemplates],
        );
      },
    });

  const { mutateAsync: updateTemplate, isPending: isUpdatingTemplate } =
    useMutation({
      mutationFn: ({
        templateId,
        updates,
      }: {
        templateId: string;
        updates: TemplateUpdates;
      }) => updateTemplateRequest(workspaceId, templateId, updates),
      onError: () => toast.error("Failed to update template"),
      onSuccess: (template) => {
        queryClient.setQueryData(
          templatesKeys.list,
          (prevTemplates: CreatedTemplate[]) => {
            const clonedTemplates = [...prevTemplates];
            const index = clonedTemplates.findIndex(
              ({ id }) => id === template.id,
            );
            clonedTemplates[index] = {
              ...template,
              metrics: clonedTemplates[index].metrics,
            };
            return clonedTemplates;
          },
        );
      },
    });

  const { mutateAsync: deleteTemplate, isPending: isDeletingTemplate } =
    useMutation({
      mutationFn: ({ templateId }: { templateId: string }) =>
        deleteTemplateRequest(workspaceId, templateId),
      onError: () => toast.error("Failed to delete template"),
      onSuccess: (_, { templateId }) => {
        queryClient.setQueryData(
          templatesKeys.list,
          (prevTemplates: CreatedTemplate[]) =>
            prevTemplates.filter(({ id }) => id !== templateId),
        );
      },
    });

  return {
    updateTemplate,
    isUpdatingTemplate,
    createTemplate,
    isCreatingTemplate,
    deleteTemplate,
    isDeletingTemplate,
  };
}
